import * as React from 'react';
import clsx from 'clsx';
import { AutoScrollbars, Button } from 'components';
import { useSelector } from 'react-redux';
import { IState } from 'services/root-reducer';
import { getContainerIcon } from 'utils';
import { ComparableLayers } from 'project-constants';
import IconZoomTo from 'static/images/icon-zoom-to.svg';
import IconDelete from 'static/images/icon-delete.svg';
import IconCompare from 'static/images/map-tools/icon-compare.svg';
import IconSortDirection from 'static/images/icon-sort-direction.svg';
import IconExcel from 'static/images/icon-excell.svg';
import { actions as objectComparisonActions } from 'services/objects-comparison/objects-comparison-service';
import { actions as overlayWindowActions } from 'services/overlay-windows/overlay-windows-service';
import { actions as containersActions } from 'services/containers/containers-service';
import { actions as mapActions } from 'services/map/map-service';
import { actions as favoritesActions } from 'services/favorites/favorites-service';
import { getOmjsProjectInstance, getFavListData, getOperationData } from 'selectors';
import { routes } from 'routes';
import { IObjectSignature } from 'types';
import { useTranslation } from 'react-i18next';

export interface IFavoritesListProps {
    compareDisabled?: boolean;
    onObjectFit?: (payload: IObjectSignature) => void;
    onObjectHighlight?: (payload: IObjectSignature) => void;
    onObjectRemove?: (payload: IObjectSignature) => void;
}

const statSelector = (state: IState) => ({
    allLayersConfig: state.app.config.publication.layers_config
});

const compareDataSelector = (state: IState) => getOperationData(state, 'OBJECTS_COMPARISON');

export const FavoritesList = (props: IFavoritesListProps) => {
    const data = useSelector(getFavListData);
    const [ sort, setSort ] = React.useState([ '', false ]);
    const { allLayersConfig } = useSelector(statSelector);
    const compareData = useSelector(compareDataSelector);
    const contIds = data.containers.map(cont => cont.id);
    const { t } = useTranslation();

    const onExportButtonClick = () => {
        let { containers, objects } = data;
        if (containers.length === 1) {
            let [ container ] = containers;
            let object_ids = objects.filter(o => o.layerCode === container.code).map(o => o.id);

            containersActions.exportObjects({
                container_code: container.code,
                object_ids: object_ids
            });
        } else {
            let signatures: IObjectSignature[] = [];
            for (let i = 0; i < containers.length; i++) {
                let container = containers[i];
                let container_objects = objects.filter(o => o.layerCode === container.code);

                for (let j = 0; j < container_objects.length; j++) {
                    let container_object = container_objects[j];
                    signatures.push({
                        layer_code: container.code,
                        object_id: container_object.id
                    });
                }
            }
            overlayWindowActions.showExportLayerChooserOverlay(signatures);
        }
    };

    const layersConfig = React.useMemo(() => {
        const layersConf = {};
        for (const lc of allLayersConfig) {
            const cont = data.containers.find(c => c.id === lc.containerId);
            if (cont) layersConf[cont.code] = lc;
        }

        return layersConf;
    }, [ contIds.join(',') ]);

    const handleRemoveItem = (evt: React.MouseEvent, layer_code: string, objId: number) => {
        evt.stopPropagation();
        favoritesActions.removeItem({ object_id: objId, layer_code });
        props.onObjectRemove?.({ layer_code, object_id: objId });
    };

    const handleToggleCompare = (evt: React.MouseEvent, layer_code: string, objId: number, active: boolean) => {
        evt.stopPropagation();
        if (active) {
            objectComparisonActions.removeItem({ layer_code, object_id: objId });
        } else {
            objectComparisonActions.addItem({ layer_code, object_id: objId });
        }
    };

    const handleFitToObject = (evt: React.MouseEvent, layer_code: string, objId: number) => {
        evt.stopPropagation();
        mapActions.fitToTarget({ layer: layer_code, objectId: objId });
        props.onObjectFit?.({ layer_code, object_id: objId });
    };

    const highlightMapFeature = (layer_code: string, objectId: number) => {
        mapActions.highlightObject({ layerCode: layer_code, objectId });
        props.onObjectHighlight?.({ layer_code, object_id: objectId });
    };

    const handleItemClick = (layer_code: string, objId: number) => {
        routes.redirect(
            routes.object,
            { layerCode: layer_code, objectId: objId },
            true,
            { fromList: true }
        );
    };

    const getSortedArray = () => {
        let objects = [ ...data.objects ];
        let desc = sort[1] ? -1 : 1;

        if (sort[0] === 'name') {
            objects.sort((aObj, bObj) => {
                const aNameField = layersConfig[aObj.layerCode].objectNameColumnCode;
                const bNameField = layersConfig[bObj.layerCode].objectNameColumnCode;
                return aObj[aNameField].localeCompare(bObj[bNameField]) * desc;
            });
        }
        return objects;
    };

    const handleSortList = (field) => {
        let newSort = sort[0] === field ? [ field, !sort[1] ] : [ field, false ];
        setSort(newSort);
    };

    const containers = data.containers.reduce((acc, item) => ({ ...acc, [item.code]: item }), {});
    const objects = getSortedArray();
    return (
        <div className="fav-list">
            <div className="fav-list__header">{t('Избранное')}</div>
            {objects.length === 0
                ? (
                    <div className="fav-list__empty">
                        {t('Список пуст')}
                    </div>
                )
                : (
                    <>
                        <div className="fav-list__controls">
                            <div className="fav-list-sort">
                                <div
                                    onClick={() => handleSortList('name')}
                                    className={clsx('fav-list-sort__item', {
                                        active: sort[0] === 'name',
                                        desc: sort[0] === 'name' && sort[1]
                                    })}
                                >
                                    {t('Название')}
                                    <IconSortDirection />
                                </div>
                            </div>
                        </div>
                        <div className="fav-list__items">
                            <AutoScrollbars>
                                <div className="fav-list__items-wrapper">
                                    {objects.map((item) => {
                                        const layerConf = layersConfig[item.layerCode];
                                        let objectName = item[layerConf.objectNameColumnCode];
                                        objectName = objectName?.replace(/&quot;/gm, '"');
                                        let compareFlag = compareData.object_ids.find(obj => (
                                            obj.layer_code === item.layerCode && obj.object_id === item.id
                                        )) !== undefined;
                                        return (
                                            <div
                                                className="fav-list__item"
                                                key={item.id}
                                                onMouseEnter={() => highlightMapFeature(item.layerCode, item.id)}
                                                onClick={() => handleItemClick(item.layerCode, item.id)}
                                            >
                                                <div className="fav-list__item-icon">
                                                    <img src={getContainerIcon(containers[item.layerCode])} alt="" />
                                                </div>
                                                <div className="fav-list__item-name">{objectName}</div>
                                                {
                                                    !props.compareDisabled && ComparableLayers.has(item.layerCode)
                                                        ? (
                                                            <div
                                                                className={clsx('fav-list__item-compare',
                                                                    compareFlag && 'active')}
                                                                onClick={evt => handleToggleCompare(
                                                                    evt, item.layerCode, item.id, compareFlag
                                                                )}
                                                            >
                                                                <IconCompare />
                                                            </div>
                                                        )
                                                        : (
                                                            <div className={clsx('fav-list__item-compare')} />
                                                        )
                                                }
                                                <div
                                                    className="fav-list__item-zoom"
                                                    onClick={evt => handleFitToObject(evt, item.layerCode, item.id)}
                                                >
                                                    <IconZoomTo />
                                                </div>
                                                <div
                                                    className="fav-list__item-delete"
                                                    onClick={evt => handleRemoveItem(evt, item.layerCode, item.id)}
                                                >
                                                    <IconDelete />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </AutoScrollbars>
                        </div>
                        <div className="fav-list__footer">
                            <Button
                                text={t('Скачать список')}
                                icon={<IconExcel />}
                                onClick={onExportButtonClick}
                            />
                        </div>
                    </>
                )}
        </div>
    );
};
